import { useState, useCallback, useRef } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import Markdown from "react-markdown";
import { HelpcenterLink } from "./helpcenter-link.component";
import { useHelpContent } from "./useHelpContent";

interface Release {
	id: string;
	created_at: string;
	app: string | null;
	title: string | null;
	summary: string | null;
	changelog_slug: string | null;
}

const formatDate = (dateStr: string) => {
	const d = new Date(dateStr);
	return d.toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

interface ReleaseNotesTabProps {
	releases: Release[];
	readIds: Set<string>;
	loading: boolean;
	hasMore: boolean;
	onMarkAsRead: (releaseId: string) => void;
	onMarkAllAsRead: () => void;
	onLoadMore: () => void;
}

export const ReleaseNotesTab: React.FC<ReleaseNotesTabProps> = ({
	releases,
	readIds,
	loading,
	hasMore,
	onMarkAsRead,
	onMarkAllAsRead,
	onLoadMore,
}) => {
	const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
	const [markdown, setMarkdown] = useState<string | null>(null);
	const [contentLoading, setContentLoading] = useState(false);
	const contentCache = useRef(new Map<string, string>());
	const { getArticle } = useHelpContent();

	const handleSelectRelease = useCallback(
		async (release: Release) => {
			setSelectedRelease(release);
			onMarkAsRead(release.id);

			if (!release.changelog_slug) {
				setMarkdown(null);
				return;
			}

			const cached = contentCache.current.get(release.changelog_slug);
			if (cached) {
				setMarkdown(cached);
				return;
			}

			setMarkdown(null);
			setContentLoading(true);
			const article = await getArticle(`changelog/${release.changelog_slug}`);
			const md = article?.markdown ?? null;
			if (md && release.changelog_slug) {
				contentCache.current.set(release.changelog_slug, md);
			}
			setMarkdown(md);
			setContentLoading(false);
		},
		[getArticle, onMarkAsRead]
	);

	if (selectedRelease) {
		return (
			<div className="flex flex-col h-full">
				<div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
					<button
						onClick={() => setSelectedRelease(null)}
						className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
						aria-label="Zurück zur Übersicht"
						data-testid="release-notes-back"
					>
						<ArrowLeft className="h-4 w-4" />
						<span>Zurück</span>
					</button>
					{selectedRelease.changelog_slug && (
						<HelpcenterLink
							path={`changelog/${selectedRelease.app || "dentlab"}/${selectedRelease.changelog_slug.replace(/-(dentlab|dentconnect)$/, "")}`}
						/>
					)}
				</div>
				<div className="flex-1 overflow-y-auto px-4 py-4">
					<div className="text-xs text-gray-400 mb-1">
						{formatDate(selectedRelease.created_at)}
					</div>
					<h2 className="text-lg font-semibold text-gray-900 mb-4">
						{selectedRelease.title ?? "Release"}
					</h2>
					{contentLoading && (
						<div className="flex items-center justify-center h-24">
							<Loader2 className="h-5 w-5 animate-spin text-gray-400" />
						</div>
					)}
					{!contentLoading && markdown && (
						<div className="prose prose-sm prose-gray max-w-none">
							<Markdown
								components={{
									img: ({ src, alt, ...props }) => (
										<img
											src={src}
											alt={alt ?? ""}
											className="rounded-lg max-w-full h-auto my-3"
											loading="lazy"
											{...props}
										/>
									),
									a: ({ href, children, ...props }) => (
										<a
											href={href}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:text-blue-700 underline"
											{...props}
										>
											{children}
										</a>
									),
									h1: ({ children, ...props }) => (
										<h1
											className="text-xl font-bold text-gray-900 mt-6 mb-3"
											{...props}
										>
											{children}
										</h1>
									),
									h2: ({ children, ...props }) => (
										<h2
											className="text-lg font-semibold text-gray-900 mt-5 mb-2"
											{...props}
										>
											{children}
										</h2>
									),
									h3: ({ children, ...props }) => (
										<h3
											className="text-base font-semibold text-gray-800 mt-4 mb-2"
											{...props}
										>
											{children}
										</h3>
									),
									p: ({ children, ...props }) => (
										<p
											className="text-sm text-gray-700 leading-relaxed mb-3"
											{...props}
										>
											{children}
										</p>
									),
									ul: ({ children, ...props }) => (
										<ul
											className="list-disc list-inside text-sm text-gray-700 mb-3 space-y-1"
											{...props}
										>
											{children}
										</ul>
									),
									li: ({ children, ...props }) => (
										<li className="text-sm text-gray-700" {...props}>
											{children}
										</li>
									),
								}}
							>
								{markdown}
							</Markdown>
						</div>
					)}
					{!contentLoading && !markdown && (
						<div className="text-sm text-gray-500 text-center py-8">
							Inhalt konnte nicht geladen werden.
						</div>
					)}
				</div>
			</div>
		);
	}

	const unread = releases.filter((r) => !readIds.has(r.id));
	const read = releases.filter((r) => readIds.has(r.id));
	const sorted = [...unread, ...read];

	return (
		<div className="flex flex-col h-full">
			{unread.length > 0 && (
				<div className="flex justify-end px-4 py-2 border-b border-gray-100">
					<button
						onClick={onMarkAllAsRead}
						className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
						aria-label="Alle als gelesen markieren"
						data-testid="release-notes-mark-all-read"
					>
						Alle als gelesen markieren
					</button>
				</div>
			)}
			<div className="flex-1 overflow-y-auto">
				{sorted.length === 0 && !loading && (
					<div className="flex items-center justify-center h-32 text-sm text-gray-500">
						Keine Neuigkeiten vorhanden
					</div>
				)}
				{sorted.map((release) => {
					const isUnread = !readIds.has(release.id);
					return (
						<button
							key={release.id}
							onClick={() => handleSelectRelease(release)}
							className="w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
							aria-label={`Release ${release.title ?? ""}`}
							data-testid={`release-item-${release.id}`}
						>
							<div className="flex items-start gap-2.5">
								{isUnread && (
									<div className="mt-1.5 w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
								)}
								<div className={isUnread ? "" : "ml-[18px]"}>
									<div className="text-xs text-gray-400 mb-0.5">
										{formatDate(release.created_at)}
									</div>
									<div
										className={`text-sm ${isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}
									>
										{release.title ?? "Release"}
									</div>
									{release.summary && (
										<div className="text-sm text-gray-500 mt-0.5 line-clamp-2">
											{release.summary}
										</div>
									)}
								</div>
							</div>
						</button>
					);
				})}
				{hasMore && (
					<div className="px-4 py-3">
						<button
							onClick={onLoadMore}
							disabled={loading}
							className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
							data-testid="release-notes-load-more"
						>
							{loading ? (
								<Loader2 className="h-4 w-4 animate-spin mx-auto" />
							) : (
								"Mehr laden"
							)}
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
